import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import AccessorySelectionModal from '../modals/AccessorySelectionModal';
import { GhostButton } from '../ui/GhostButton';

import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { BASE_UPLOAD_URL } from '../../config';

const AccessoriesTab = () => {
    const {
        productData,
        components,
        addComponent,
        updateComponent,
        deleteComponent,
        canEditContent,
        activeCharge // Add activeCharge
    } = usePIM();

    // Filter for Accessories (Global or Specific to Active Charge)
    const accessories = components.filter(c =>
        c.role === 'accessory' &&
        (!c.charge_id || (activeCharge && c.charge_id === activeCharge.id))
    );

    const [isModalOpen, setIsModalOpen] = useState(false);

    // Sorting
    const { items: sortedAccessories, requestSort, sortConfig } = useSortableData(accessories);

    const handleAddAccessory = async (items) => {
        // items is array of { id, chargeId }
        for (const item of items) {
            await addComponent(productData.id, item.id, 'accessory', item.chargeId);
        }
        setIsModalOpen(false);
    };

    const handleTogglePackageContent = async (acc) => {
        const newVal = !acc.is_package_content;
        await updateComponent(productData.id, acc.id, { is_package_content: newVal });
    };

    const handleDelete = async (rowId) => {
        await deleteComponent(productData.id, rowId);
    };

    return (
        <div className="h-full flex flex-col">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-bold text-white">Accessories</h2>
                    <p className="text-xs text-stone-500 mt-1">Manage product accessories and package contents.</p>
                </div>
                <div className="flex gap-2">
                    {canEditContent && (
                        <GhostButton onClick={() => setIsModalOpen(true)}>
                            <Plus size={16} /> Add Accessory
                        </GhostButton>
                    )}
                </div>
            </div>

            <div className="bg-black/20 border border-white/5 rounded-xl overflow-hidden shadow-xl flex-1 flex flex-col">
                <div className="overflow-y-auto flex-1">
                    <table className="w-full text-left border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr>
                                <SortableHeader label="Accessory Item" sortKey="model_name" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="SKU" sortKey="sku" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="EAN" sortKey="ean" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Status" sortKey="status" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Category" sortKey="category" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Role" sortKey="product_types" currentSort={sortConfig} onSort={requestSort} />
                                <SortableHeader label="Package Content" sortKey="is_package_content" currentSort={sortConfig} onSort={requestSort} className="text-center" />
                                <th className="bg-black/40 text-stone-500 text-xs uppercase font-semibold px-4 py-3 border-b border-white/5 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-800">
                            {sortedAccessories.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="p-8 text-center text-stone-500">
                                        No accessories added yet.
                                    </td>
                                </tr>
                            ) : (
                                sortedAccessories.map((acc) => (
                                    <tr key={acc.id} className="hover:bg-white/5 transition-colors border-b border-white/5 last:border-0">
                                        <td className="px-4 py-2">
                                            <div className="flex items-center gap-3">
                                                {acc.image_url ? (
                                                    <img src={`${BASE_UPLOAD_URL}/${acc.image_url.replace(/^\/+/, '')}`} alt={acc.model_name} className="w-8 h-8 object-cover rounded bg-stone-800" />
                                                ) : (
                                                    <div className="w-8 h-8 bg-stone-800 rounded flex items-center justify-center text-xs text-stone-500">
                                                        No Img
                                                    </div>
                                                )}
                                                <span className="text-stone-300 font-medium text-sm">{acc.model_name}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-sm text-stone-300">{acc.sku}</td>
                                        <td className="px-4 py-2 text-sm text-stone-300">{acc.ean || '-'}</td>
                                        <td className="px-4 py-2 text-sm">
                                            {acc.status ? (
                                                <span
                                                    className="px-2 py-1 rounded text-xs font-medium border"
                                                    style={{
                                                        backgroundColor: `${acc.status_color}33`,
                                                        color: '#fff',
                                                        borderColor: `${acc.status_color}4d`
                                                    }}
                                                >
                                                    {acc.accessory_batch_number ? `${acc.accessory_batch_number} (${acc.status})` : acc.status}
                                                </span>
                                            ) : (
                                                <span className="text-stone-500">-</span>
                                            )}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-stone-300">{acc.category || '-'}</td>
                                        <td className="px-4 py-2">
                                            {(() => {
                                                try {
                                                    const types = typeof acc.product_types === 'string'
                                                        ? JSON.parse(acc.product_types)
                                                        : acc.product_types;

                                                    if (Array.isArray(types) && types.length > 0) {
                                                        return (
                                                            <div className="flex flex-wrap gap-1">
                                                                {types.map((type, idx) => (
                                                                    <span key={idx} className="px-2 py-0.5 rounded text-[10px] font-medium border uppercase bg-blue-900/20 border-blue-800 text-blue-300">
                                                                        {type.replace('_', ' ')}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        );
                                                    }
                                                    return <span className="text-stone-500">-</span>;
                                                } catch (e) {
                                                    return <span className="text-stone-500">-</span>;
                                                }
                                            })()}
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <input
                                                type="checkbox"
                                                checked={!!acc.is_package_content}
                                                onChange={() => handleTogglePackageContent(acc)}
                                                className="accent-stone-500 w-4 h-4 cursor-pointer"
                                                disabled={!canEditContent}
                                                title={canEditContent ? "Toggle Package Content" : "Read-only"}
                                            />
                                        </td>
                                        <td className="px-4 py-2 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => window.open(`/?sku=${acc.sku}${acc.accessory_charge_id ? `&charge_id=${acc.accessory_charge_id}` : ''}`, '_blank')}
                                                    className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-blue-400 hover:border-blue-900/50 hover:bg-blue-900/10 flex items-center justify-center transition"
                                                    title="View Product"
                                                >
                                                    <ExternalLink size={14} />
                                                </button>
                                                {canEditContent && (
                                                    <button
                                                        onClick={() => handleDelete(acc.id)}
                                                        className="w-7 h-7 rounded border border-stone-600 text-stone-400 hover:text-red-400 hover:border-red-900/50 hover:bg-red-900/10 flex items-center justify-center transition"
                                                        title="Remove Accessory"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <AccessorySelectionModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onAdd={handleAddAccessory}
            />
        </div>
    );
};

export default AccessoriesTab;
